import { FC } from 'react';
import {
  AllieBadgeStyled,
  ContentExpandedVariableWrapper,
  ProcessVariableContentWrapper,
  StatsContainer,
  StyledAllieChip,
  StyledIconButton,
  StyledValue,
  ValueAndStatusContainer,
  VariableValueContainer,
} from '../ProcessVariableCard.styles';
import { ContentProcessVariableProps, StatusProcessVariable } from '../ProcessVariableCard.types';
import { AllieText } from '../../../../components';
import { AllieWarningOutlineIcon, AllieEditPencilIcon } from '../../../../styling/icons';
import { AllieColors } from '../../../../common/color.types';
import { ContentOutdated } from './ContentOutdated';
import { ContentExpandedVariableMobile } from './mobile/ContentExpandedVariable.mobile';
import { VariableTagsRow } from './VariableTagsRow';

interface ContentExpandedVariableProps extends ContentProcessVariableProps {
  name: string;
  lastChangeAt: string;
  labelValues: string;
  backgroundColor: AllieColors;
  status: StatusProcessVariable;
  textOutdated: string;
  isMobile: boolean;
  tags?: { id: number; name: string }[];
  onEditTags?: () => void;
  editTagsAriaLabel?: string;
}

export const ContentExpandedVariable: FC<ContentExpandedVariableProps> = ({
  fontColor,
  graphTrendPreview,
  machineName,
  value,
  borderChipColor,
  statusText,
  name,
  lastChangeAt,
  labelValues,
  backgroundColor,
  status,
  textOutdated,
  isMobile,
  favoriteView,
  range,
  avgLabel,
  avgValue,
  minLabel,
  minValue,
  maxLabel,
  maxValue,
  withoutDataLabel,
  machineStoppedLabel,
  isCustomType,
  isMetricOnOff,
  tags,
  onEditTags,
  editTagsAriaLabel,
}) => {
  if (isMobile) {
    return (
      <ContentExpandedVariableMobile
        fontColor={fontColor}
        graphTrendPreview={graphTrendPreview}
        machineName={machineName}
        value={value}
        borderChipColor={borderChipColor}
        statusText={statusText}
        name={name}
        lastChangeAt={lastChangeAt}
        labelValues={labelValues}
        backgroundColor={backgroundColor}
        status={status}
        textOutdated={textOutdated}
        favoriteView={favoriteView}
        range={range}
        isMobile={isMobile}
        avgLabel={avgLabel}
        avgValue={avgValue}
        minLabel={minLabel}
        minValue={minValue}
        maxLabel={maxLabel}
        maxValue={maxValue}
        withoutDataLabel={withoutDataLabel}
        machineStoppedLabel={machineStoppedLabel}
        isCustomType={isCustomType}
        isMetricOnOff={isMetricOnOff}
        tags={tags}
        onEditTags={onEditTags}
        editTagsAriaLabel={editTagsAriaLabel}
      />
    );
  } else {
    return (
      <ProcessVariableContentWrapper isMobile={isMobile}>
        <ContentExpandedVariableWrapper>
          <div>
            <AllieText
              color="neutral-70"
              type="body2"
              typeSet={[{ resolution: 1600, type: 'body1' }]}
            >
              {machineName}
            </AllieText>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <AllieText
                color="neutral-70"
                type="h5"
                typeSet={[{ resolution: 1600, type: 'button-normal' }]}
                weight={600}
              >
                {name}
              </AllieText>
              {onEditTags && (
                <StyledIconButton
                  size="xs"
                  aria-label={editTagsAriaLabel}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    onEditTags();
                  }}
                >
                  <AllieEditPencilIcon />
                </StyledIconButton>
              )}
            </div>
            {status === 'machineStopped' && (
              <AllieBadgeStyled>{machineStoppedLabel}</AllieBadgeStyled>
            )}
            {tags && tags.length > 0 && <VariableTagsRow tags={tags} />}
          </div>
          <VariableValueContainer $backgroundColor={backgroundColor}>
            <div className="status-value-container">
              <AllieText
                color="neutral-70"
                type="body3"
                weight={600}
                typeSet={[
                  { resolution: 1600, type: 'body2' },
                  { resolution: 1920, type: 'body1' },
                ]}
              >
                {labelValues}
              </AllieText>
              <ValueAndStatusContainer>
                {status !== 'outdated' && (
                  <div className="value-container">
                    <StyledValue
                      color={fontColor}
                      type="h3"
                      typeSet={[
                        { resolution: 1280, type: 'h25' },
                        { resolution: 1600, type: 'h2' },
                      ]}
                    >
                      {value ?? '-'}
                    </StyledValue>
                    {status !== 'stationStopped' && status !== 'machineStopped' && (
                      <AllieWarningOutlineIcon
                        style={{ fontSize: '24px', color: `var(--allie-color-${fontColor})` }}
                      />
                    )}
                  </div>
                )}
                {statusText && (
                  <StyledAllieChip
                    outline
                    corner="rounded"
                    style={{
                      border: `1.5px solid var(--allie-color-${borderChipColor})`,
                      cursor: 'default',
                    }}
                  >
                    <AllieText
                      type="body3"
                      color={fontColor}
                      weight={500}
                      style={{ textWrap: 'nowrap' }}
                    >
                      {statusText}
                    </AllieText>
                  </StyledAllieChip>
                )}
              </ValueAndStatusContainer>
            </div>
            <StatsContainer
              isMobile={isMobile}
              isCustomType={isCustomType!}
              isMetricOnOff={isMetricOnOff!}
            >
              <div>
                <AllieText
                  color="neutral-70"
                  type="body2"
                  typeSet={[{ resolution: 1600, type: 'body1' }]}
                >
                  {avgLabel}
                </AllieText>
                <AllieText
                  color="neutral-70"
                  type="body2"
                  typeSet={[{ resolution: 1600, type: 'body1' }]}
                  weight={600}
                >
                  {avgValue}
                </AllieText>
              </div>
              <div style={{ display: 'flex', gap: 'var(--allie-spacing-md)' }}>
                <div>
                  <AllieText
                    color="neutral-70"
                    type="body2"
                    typeSet={[{ resolution: 1600, type: 'body1' }]}
                  >
                    {minLabel}
                  </AllieText>
                  <AllieText
                    color="neutral-70"
                    type="body2"
                    typeSet={[{ resolution: 1600, type: 'body1' }]}
                    weight={600}
                  >
                    {minValue}
                  </AllieText>
                </div>
                <div>
                  <AllieText
                    color="neutral-70"
                    type="body2"
                    typeSet={[{ resolution: 1600, type: 'body1' }]}
                  >
                    {maxLabel}
                  </AllieText>
                  <AllieText
                    color="neutral-70"
                    type="body2"
                    typeSet={[{ resolution: 1600, type: 'body1' }]}
                    weight={600}
                  >
                    {maxValue}
                  </AllieText>
                </div>
              </div>
            </StatsContainer>
            <AllieText
              color="neutral-50"
              type="body2"
              typeSet={[{ resolution: 1600, type: 'body1' }]}
            >
              {lastChangeAt}
            </AllieText>
          </VariableValueContainer>
        </ContentExpandedVariableWrapper>
        {status === 'outdated' ? (
          <ContentOutdated text={textOutdated} title={withoutDataLabel} />
        ) : (
          graphTrendPreview
        )}
      </ProcessVariableContentWrapper>
    );
  }
};
